import { ExternalLink, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/common/StatusBadge";
import { fmtMinutes } from "@/lib/format";
import { ROUTE_SOURCE, directionsUrl, fmtKm, hasPlace, placeUrl } from "@/lib/maps";
import { RouteMap, markersFromLogistics } from "@/components/maps/RouteMap";
import { cn } from "@/lib/utils";

const Stop = ({ marker, tone, place, label, last = false, testId }) => (
  <li className="relative flex gap-3 pb-4 last:pb-0" data-testid={testId}>
    {!last ? <span className="absolute left-[13px] top-7 bottom-0 w-px bg-border" aria-hidden="true" /> : null}
    <span className={cn("num relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold", tone === "primary" ? "bg-primary text-primary-foreground" : tone === "dark" ? "bg-brand-charcoal text-white" : "bg-secondary text-foreground")} aria-hidden="true">
      {marker}
    </span>
    <div className="min-w-0 flex-1 pt-0.5">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      {hasPlace(place) ? (
        <a href={placeUrl(place)} target="_blank" rel="noopener noreferrer" className="focus-ring group inline-block rounded-sm" data-testid={`${testId}-link`}>
          <span className="block text-sm font-medium leading-5 group-hover:underline">{place.name || place.address}</span>
          {place.name && place.address && place.address !== place.name ? <span className="block text-xs leading-5 text-muted-foreground">{place.address}</span> : null}
        </a>
      ) : (
        <p className="text-sm text-muted-foreground">Ikkje sett</p>
      )}
    </div>
  </li>
);

/** Logistikk på ordra: rute med stopp, bil, tid, avstand og tilkomst. Synleg for alle roller (ingen prisar). */
export const LogistikkCard = ({ logistics, className, testId = "logistikk" }) => {
  const lg = logistics || {};
  const stops = lg.stops || [];
  const hasAny = hasPlace(lg.pickup) || hasPlace(lg.delivery) || stops.some(hasPlace);
  const link = directionsUrl({ origin: lg.pickup, destination: lg.delivery, stops });
  const source = lg.distance_source ? ROUTE_SOURCE[lg.distance_source] : null;
  const letters = "ABCDEFGHIJ";
  if (!hasAny && !lg.vehicle_name && !lg.access_note) {
    return (
      <div className={cn("rounded-lg border border-dashed border-border bg-card px-4 py-6 text-center text-sm text-muted-foreground", className)} data-testid={`${testId}-empty`}>
        Ingen stader eller rute er lagde inn på ordra.
      </div>
    );
  }
  return (
    <div className={cn("rounded-lg border border-border bg-card", className)} data-testid={testId}>
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5 md:px-5">
        <h2 className="text-[13px] font-semibold leading-5">Stader og rute</h2>
        {link ? (
          <Button variant="outline" size="sm" className="h-8 bg-card" asChild>
            <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-navigate`}><Navigation className="mr-1.5 h-3.5 w-3.5" /> Naviger</a>
          </Button>
        ) : null}
      </div>
      {markersFromLogistics(lg).length ? (
        <div className="border-b border-border p-2 md:p-3">
          <RouteMap markers={markersFromLogistics(lg)} geometry={lg.route_geometry} source={lg.distance_source} heightClassName="h-52 md:h-72" testId={`${testId}-map`} />
        </div>
      ) : null}
      <div className="grid gap-4 p-4 md:grid-cols-[minmax(0,1fr)_220px] md:p-5">
        <ol className="min-w-0">
          {hasPlace(lg.pickup) ? <Stop marker="A" tone="dark" label="Hentestad" place={lg.pickup} testId={`${testId}-pickup`} /> : null}
          {stops.filter(hasPlace).map((s, i) => (
            <Stop key={i} marker={letters[i + 1]} label={`Stopp ${i + 1}`} place={s} testId={`${testId}-stop-${i}`} />
          ))}
          <Stop marker={letters[(hasPlace(lg.pickup) ? 1 : 0) + stops.filter(hasPlace).length]} tone="primary" label="Leverings-/arbeidsstad" place={lg.delivery} last testId={`${testId}-delivery`} />
        </ol>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm md:grid-cols-1">
          <div>
            <dt className="text-xs text-muted-foreground">Avstand éin veg</dt>
            <dd className="tabular mt-0.5 flex flex-wrap items-center gap-1.5 font-medium" data-testid={`${testId}-distance`}>
              {lg.distance_km !== null && lg.distance_km !== undefined ? <>{fmtKm(lg.distance_km)}{lg.duration_min ? <span className="text-muted-foreground">· {fmtMinutes(Math.round(lg.duration_min))}</span> : null}</> : "–"}
              {source ? <Chip tone={source.tone} outline>{source.label}</Chip> : null}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Bil / køyretøy</dt>
            <dd className="mt-0.5 font-medium" data-testid={`${testId}-vehicle`}>{lg.vehicle_name || "Ikkje vald"}</dd>
          </div>
          {lg.planned_time ? (
            <div>
              <dt className="text-xs text-muted-foreground">Planlagt klokkeslett</dt>
              <dd className="tabular mt-0.5 font-medium" data-testid={`${testId}-time`}>{lg.planned_time}</dd>
            </div>
          ) : null}
          {lg.access_note ? (
            <div className="col-span-2 md:col-span-1">
              <dt className="text-xs text-muted-foreground">Tilkomst / merknad</dt>
              <dd className="mt-0.5 whitespace-pre-wrap leading-5" data-testid={`${testId}-access`}>{lg.access_note}</dd>
            </div>
          ) : null}
        </dl>
      </div>
      {link ? (
        <div className="border-t border-border px-4 py-2 md:px-5">
          <a href={link} target="_blank" rel="noopener noreferrer" className="focus-ring inline-flex items-center gap-1.5 rounded-sm text-xs text-muted-foreground hover:text-foreground">
            <ExternalLink className="h-3.5 w-3.5" /> Opne heile ruta i Google Maps
          </a>
        </div>
      ) : null}
    </div>
  );
};
